import { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Fade,
  LinearProgress,
  Select,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";
import axios from "axios";
import { styled, keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import EventIcon from "@mui/icons-material/Event";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled(Card)(({ priority }) => ({
  animation: `${fadeIn} 0.4s ease-out`,
  transition: "all 0.3s cubic-bezier(.25,.8,.25,1)",
  "&:hover": {
    transform: "scale(1.04)",
    boxShadow: `0 12px 24px rgba(111,217,67,0.18), 0 6px 12px rgba(0,0,0,0.18)`,
  },
  borderRight: `8px solid ${priorityColors[priority] || "#333"}`,
}));

const ColumnHeader = styled(Box)({
  padding: "16px 20px",
  background: "linear-gradient(135deg, #232526 0%, #6fd943 100%)",
  borderRadius: "12px 12px 0 0",
  boxShadow: "0 6px 16px rgba(111,217,67,0.10), 0 2px 8px rgba(0,0,0,0.18)",
  marginBottom: "8px",
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.15rem",
  letterSpacing: "0.5px",
  borderBottom: "2px solid #6fd943",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "56px",
});

const ColumnContainer = styled(Box)({
  background: "#25282b",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  padding: "8px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#1a1d20",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#6fd943",
    borderRadius: "3px",
  },
});

const priorityColors = {
  Low: "#2e7d32",
  Normal: "#1976d2",
  High: "#ed6c02",
  Critical: "#d32f2f",
};

const columns = ["البداية", "قيد التنفيذ", "منتهية", "مؤرشف"];

const columnColors = {
  "البداية": {
    header: "linear-gradient(135deg, #232526 0%, #1976d2 100%)",
    background: "#232a34"
  },
  "قيد التنفيذ": {
    header: "linear-gradient(135deg, #232526 0%, #ed6c02 100%)",
    background: "#2d2623"
  },
  "منتهية": {
    header: "linear-gradient(135deg, #232526 0%, #2e7d32 100%)",
    background: "#232d25"
  },
  "مؤرشف": {
    header: "linear-gradient(135deg, #232526 0%, #d32f2f 100%)",
    background: "#2d2323"
  }
};

// Styled components for filters
const FilterBar = styled(Box)({
  display: "flex",
  flexDirection: "column",
  background: "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
  borderRadius: 16,
  padding: "20px 24px",
  marginBottom: 24,
  boxShadow: "0 4px 20px rgba(111,217,67,0.12), 0 2px 8px rgba(0,0,0,0.3)",
  border: "1px solid rgba(111,217,67,0.15)",
  position: "relative",
  transition: "all 0.3s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: "linear-gradient(90deg, #6fd943, #4caf50, #6fd943)",
    borderRadius: "16px 16px 0 0",
  },
});

const FilterHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
  padding: "8px 0",
  "&:hover": {
    "& .filter-toggle-icon": {
      transform: "scale(1.1)",
    },
  },
});

const FilterPanel = styled(Box)(({ open }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  overflow: "hidden",
  transition: "all 0.3s ease",
  maxHeight: open ? "1000px" : "0",
  opacity: open ? 1 : 0,
  paddingTop: open ? "16px" : "0",
  borderTop: open ? "1px solid rgba(111,217,67,0.2)" : "none",
}));

const FilterRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  "@media (max-width: 768px)": {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 8,
  },
});

const FilterGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "16px",
  background: "rgba(35, 37, 38, 0.3)",
  borderRadius: 12,
  border: "1px solid rgba(111,217,67,0.1)",
});

const FilterGroupTitle = styled(Typography)({
  color: "#6fd943",
  fontWeight: 700,
  fontSize: "14px",
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const StyledSelect = styled(Select)({
  minWidth: 150,
  background: "rgba(35, 37, 38, 0.8)",
  color: "#fff",
  borderRadius: 10,
  fontWeight: 600,
  transition: "all 0.3s ease",
  "& .MuiSelect-icon": {
    color: "#6fd943",
    transition: "transform 0.2s ease",
  },
  "&:hover": {
    background: "rgba(38, 42, 45, 0.9)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(111,217,67,0.2)",
    "& .MuiSelect-icon": {
      transform: "rotate(180deg)",
    },
  },
  "&.Mui-focused": {
    background: "rgba(38, 42, 45, 1)",
    borderColor: "#6fd943",
    boxShadow: "0 0 0 2px rgba(111,217,67,0.2)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(111,217,67,0.3)",
    borderWidth: "1.5px",
  },
  "& .MuiSelect-select": {
    padding: "8px 12px",
    fontSize: "14px",
  },
  marginRight: 8,
  "@media (max-width: 768px)": {
    minWidth: "100%",
    marginRight: 0,
    marginBottom: 8,
  },
});

const TicketCounter = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "linear-gradient(135deg, #6fd943 0%, #4caf50 100%)",
  color: "#fff",
  padding: "8px 16px",
  borderRadius: 20,
  fontWeight: 700,
  fontSize: "14px",
  boxShadow: "0 2px 8px rgba(111,217,67,0.3)",
  border: "1px solid rgba(255,255,255,0.2)",
  minWidth: "fit-content",
  "& .counter-number": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 900,
  },
});

const FilterLabel = styled(Typography)({
  color: "#6fd943",
  fontWeight: 700,
  fontSize: "16px",
  marginRight: 8,
  textShadow: "0 0 8px rgba(111,217,67,0.3)",
});

const TicketsForIT = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState({
    priority: "",
    status: "",
    company: "",
    department: "",
  });

  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // الشهر الحالي كافتراضي
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });
  const [departments, setDepartments] = useState([]); // <-- Add departments state

  // Get current IT employee ID from localStorage
  let currentUser = {};
  try {
    const stored = localStorage.getItem("data");
    if (stored) currentUser = JSON.parse(stored);
  } catch (e) {
    currentUser = {};
  }
  const currentEmployeeId = currentUser.employee_id;

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axios.get(
          "https://backenderpforseg.samielmetwali.com/tickets/getAllTicketsForAllUsers.php"
        );
        if (response.data.success) {
          const data = response.data.data.map((t) => {
            let status;
            switch (t.status) {
              case "Open":
                status = "البداية";
                break;
              case "In Progress":
                status = "قيد التنفيذ";
                break;
              case "Closed":
                status = "منتهية";
                break;
              case "Archived":
              case "مؤرشف":
                status = "مؤرشف";
                break;
              default:
                status = columns.includes(t.status) ? t.status : "البداية";
            }
            return { ...t, status, priority: t.priority || "Normal" };
          });
          setTickets(data);
        } else {
          setTickets([]);
        }
      } catch (error) {
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };
    const fetchDepartments = async () => {
      try {
        const response = await axios.get("https://backenderpforseg.samielmetwali.com/getDepartments.php");
        if (response.data.success) {
          setDepartments(response.data.departments);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        setDepartments([]);
      }
    };
    fetchTickets();
    fetchDepartments(); // <-- Fetch departments
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (searchText === "") {
      setDebouncedSearch("");
      return;
    }
    
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  // Set current month when tickets are loaded
  useEffect(() => {
    if (tickets.length > 0) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      setSelectedMonth(currentMonth);
    }
  }, [tickets]);

  // Filter tickets for this IT employee
  const myTickets = useMemo(() => {
    const filtered = tickets.filter(
      (t) => String(t.assigned_to) === String(currentEmployeeId)
    );

    return filtered.filter((ticket) => {
      // Priority filter
      if (filter.priority && ticket.priority !== filter.priority) return false;
      
      // Status filter
      if (filter.status && ticket.status !== filter.status) return false;
      
      // Company filter
      if (filter.company && ticket.company !== filter.company) return false;
      
      // Department filter
      if (filter.department && ticket.department !== filter.department) return false;
      
      // Month filter
      if (selectedMonth && ticket.created_at && ticket.created_at.slice(0, 7) !== selectedMonth) return false;
      
      // Date range filter
      if (dateRange.fromDate && ticket.created_at < dateRange.fromDate) return false;
      if (dateRange.toDate && ticket.created_at > dateRange.toDate + " 23:59:59") return false;
      
      // Text search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const titleMatch = ticket.title && ticket.title.toLowerCase().includes(searchLower);
        const descriptionMatch = ticket.description && ticket.description.toLowerCase().includes(searchLower);
        const usernameMatch = ticket.username && ticket.username.toLowerCase().includes(searchLower);
        
        if (!titleMatch && !descriptionMatch && !usernameMatch) return false;
      }
      
      return true;
    });
  }, [tickets, currentEmployeeId, filter, selectedMonth, dateRange, debouncedSearch]);

  // Get available months from tickets
  const availableMonths = useMemo(() => {
    const months = Array.from(
      new Set(
        tickets
          .filter((t) => String(t.assigned_to) === String(currentEmployeeId))
          .map((t) => t.created_at && t.created_at.slice(0, 7)) // "YYYY-MM"
          .filter(Boolean)
      )
    ).sort().reverse(); // ترتيب تنازلي (الأحدث أولاً)
    
    return months;
  }, [tickets, currentEmployeeId]);

  // Clear all filters function
  const clearAllFilters = () => {
    setFilter({
      priority: "",
      status: "",
      company: "",
      department: "",
    });
    setSelectedMonth(new Date().toISOString().slice(0, 7)); // إعادة تعيين للشهر الحالي
    setDateRange({
      fromDate: "",
      toDate: "",
    });
    setSearchText("");
    setDebouncedSearch("");
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() =>
    filter.priority ||
    filter.status ||
    filter.company ||
    filter.department ||
    selectedMonth !== new Date().toISOString().slice(0, 7) || // إذا كان الشهر المختار غير الشهر الحالي
    dateRange.fromDate ||
    dateRange.toDate ||
    debouncedSearch,
    [filter, selectedMonth, dateRange, debouncedSearch]
  );

  const total = myTickets.length;
  const lowPriority = myTickets.filter((t) => t.priority === "Low").length;
  const normalPriority = myTickets.filter(
    (t) => t.priority === "Normal"
  ).length;
  const highPriority = myTickets.filter((t) => t.priority === "High").length;
  const criticalPriority = myTickets.filter(
    (t) => t.priority === "Critical"
  ).length;

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Find the ticket
    const ticketId = draggableId;
    const ticket = myTickets.find((t) => String(t.id) === ticketId);
    if (!ticket) return;

    // Update status
    const newStatus = destination.droppableId;
    const updatedTicket = { ...ticket, status: newStatus };

    // Update state
    setTickets((prev) =>
      prev.map((t) =>
        String(t.id) === ticketId ? { ...t, status: newStatus } : t
      )
    );

    // Optionally, update backend
    try {
      await axios.post(
        "https://backenderpforseg.samielmetwali.com/tickets/updateStatus.php",
        {
          id: ticketId,
          status: newStatus,
        }
      );
    } catch (e) {
      // Optionally handle error and revert state
    }
  };

  const priorityLabels = {
    Low: "منخفضة",
    Normal: "عادية",
    High: "مرتفعة",
    Critical: "حرجة",
  };

  return (
    <Fade in={true} timeout={800}>
      <Paper
        style={{
          padding: "24px",
          marginBottom: "16px",
          fontFamily: "Cairo, sans-serif",
          direction: "rtl",
          backgroundColor: "#1a1d20",
          color: "white",
          overflowX: "auto",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          style={{
            fontFamily: "Cairo, sans-serif",
            color: "#6fd943",
            fontWeight: 700,
            marginBottom: "24px",
            textAlign: "center",
            textShadow: "0 0 10px rgba(111, 217, 67, 0.4)",
          }}
        >
          تذاكر الموظف (IT)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            {
              label: "عدد التذاكر",
              value: total,
              color: "#fff",
            },
            {
              label: "منخفضة",
              value: lowPriority,
              color: "#388e3c",
            },
            {
              label: "عادية",
              value: normalPriority,
              color: "#1976d2",
            },
            {
              label: "مرتفعة",
              value: highPriority,
              color: "#ed6c02",
            },
            {
              label: "حرجة",
              value: criticalPriority,
              color: "#d32f2f",
            },
          ].map((priority, index) => (
            <Grid item xs={12} md={2.4} key={index}>
              <Card
                sx={{
                  background:
                    "linear-gradient(to bottom right, #2d2d2d, #1f1f1f)",
                  color: "#fff",
                  borderRadius: "8px",
                  boxShadow: 3,
                  borderTop: `4px solid ${priority.color}`,
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ opacity: 0.8, mb: 0.5 }}
                      >
                        {priority.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {priority.value}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={total > 0 ? (priority.value / total) * 100 : 0}
                    sx={{
                      mt: 2,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(priority.color, 0.2),
                      "& .MuiLinearProgress-bar": {
                        bgcolor: priority.color,
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* Filter Bar */}
        <FilterBar>
          <FilterHeader onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FilterAltIcon sx={{ color: "#6fd943", fontSize: 28 }} />
              <FilterLabel>تصفية التذاكر</FilterLabel>
              {hasActiveFilters && (
                <Box
                  sx={{
                    background: "#6fd943",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {Object.values(filter).filter(Boolean).length +
                    (selectedMonth !== new Date().toISOString().slice(0, 7) ? 1 : 0) +
                    (dateRange.fromDate || dateRange.toDate ? 1 : 0) +
                    (searchText ? 1 : 0)}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TicketCounter>
                <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                <span>التذاكر المطابقة:</span>
                <span className="counter-number">{myTickets.length}</span>
              </TicketCounter>
              {filterPanelOpen ? (
                <ExpandLessIcon
                  className="filter-toggle-icon"
                  sx={{ color: "#6fd943", fontSize: 24 }}
                />
              ) : (
                <ExpandMoreIcon
                  className="filter-toggle-icon"
                  sx={{ color: "#6fd943", fontSize: 24 }}
                />
              )}
            </Box>
          </FilterHeader>

          <FilterPanel open={filterPanelOpen}>
            {/* Basic Filters Group */}
            <FilterGroup>
              <FilterGroupTitle>
                <ApartmentIcon /> الفلاتر الأساسية
              </FilterGroupTitle>
              <FilterRow>
                <StyledSelect
                  value={filter.priority}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, priority: e.target.value }))
                  }
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    <PriorityHighIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                    الأولويات
                  </MenuItem>
                  <MenuItem value="Low">منخفضة</MenuItem>
                  <MenuItem value="Normal">عادية</MenuItem>
                  <MenuItem value="High">مرتفعة</MenuItem>
                  <MenuItem value="Critical">حرجة</MenuItem>
                </StyledSelect>

                <StyledSelect
                  value={filter.status}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, status: e.target.value }))
                  }
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    <EventIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                    الحالات
                  </MenuItem>
                  <MenuItem value="البداية">البداية</MenuItem>
                  <MenuItem value="قيد التنفيذ">قيد التنفيذ</MenuItem>
                  <MenuItem value="منتهية">منتهية</MenuItem>
                  <MenuItem value="مؤرشف">مؤرشف</MenuItem>
                </StyledSelect>

                <StyledSelect
                  value={filter.company}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, company: e.target.value }))
                  }
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    <BusinessIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                    الشركات
                  </MenuItem>
                  <MenuItem value="SEG">SEG</MenuItem>
                  <MenuItem value="ElBatt">ElBatt</MenuItem>
                  <MenuItem value="ZBooma">ZBooma</MenuItem>
                  <MenuItem value="Sapaaq">Sapaaq</MenuItem>
                  <MenuItem value="Moharek">Moharek</MenuItem>
                  <MenuItem value="Daad">Daad</MenuItem>
                </StyledSelect>

                <StyledSelect
                  value={filter.department}
                  onChange={(e) =>
                    setFilter((f) => ({
                      ...f,
                      department: e.target.value,
                    }))
                  }
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    <ApartmentIcon sx={{ color: "#6fd943", mr: 1 }} /> كل الأقسام
                  </MenuItem>
                  {departments.map((dep) => (
                    <MenuItem key={dep.id} value={dep.name}>
                      {dep.name}
                    </MenuItem>
                  ))}
                </StyledSelect>

                <StyledSelect
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    <EventIcon sx={{ color: "#6fd943", mr: 1 }} /> كل الشهور
                  </MenuItem>
                  {availableMonths.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FilterRow>
            </FilterGroup>

            {/* Search and Date Filters Group */}
            <FilterGroup>
              <FilterGroupTitle>
                <SearchIcon /> البحث والتواريخ
              </FilterGroupTitle>
              <FilterRow>
                <TextField
                  placeholder="البحث في العنوان أو الوصف أو الاسم..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      background: "rgba(35, 37, 38, 0.8)",
                      color: "#fff",
                      borderRadius: 2,
                      "&:hover": {
                        background: "rgba(38, 42, 45, 0.9)",
                      },
                      "&.Mui-focused": {
                        background: "rgba(38, 42, 45, 1)",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(111,217,67,0.3)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6fd943",
                    },
                    "@media (max-width: 768px)": {
                      minWidth: "100%",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: "#6fd943", mr: 1 }} />
                    ),
                  }}
                />
              </FilterRow>

              <FilterRow>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <DateRangeIcon sx={{ color: "#6fd943" }} />
                  <TextField
                    type="date"
                    value={dateRange.fromDate}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                    size="small"
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        background: "rgba(35, 37, 38, 0.8)",
                        color: "#fff",
                        borderRadius: 2,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(111,217,67,0.3)",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { color: "#6fd943" },
                    }}
                    label="من تاريخ"
                  />
                  <Typography sx={{ color: "#6fd943" }}>إلى</Typography>
                  <TextField
                    type="date"
                    value={dateRange.toDate}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                    size="small"
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        background: "rgba(35, 37, 38, 0.8)",
                        color: "#fff",
                        borderRadius: 2,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(111,217,67,0.3)",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { color: "#6fd943" },
                    }}
                    label="إلى تاريخ"
                  />
                </Box>
              </FilterRow>
            </FilterGroup>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={clearAllFilters}
                  sx={{
                    color: "#6fd943",
                    borderColor: "#6fd943",
                    borderRadius: "12px",
                    fontWeight: 600,
                    px: 4,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "rgba(111,217,67,0.1)",
                      borderColor: "#4caf50",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  مسح جميع الفلاتر
                </Button>
              </Box>
            )}
          </FilterPanel>
        </FilterBar>
        
        {loading ? (
          <Box style={{ minWidth: "900px" }}>
            <Grid container spacing={3}>
              {columns.map((col) => (
                <Grid item xs={12} md={4} key={col}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={40}
                    sx={{ bgcolor: "#2a2e31" }}
                  />
                  <Box mt={2}>
                    {[1, 2, 3].map((item) => (
                      <Skeleton
                        key={item}
                        variant="rounded"
                        width="100%"
                        height={180}
                        sx={{
                          bgcolor: "#2a2e31",
                          mb: 2,
                          borderRadius: "8px",
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid
              container
              spacing={3}
              alignItems="stretch"
              style={{ minHeight: "80vh" }}
            >
              {columns.map((col) => (
                <Grid
                  item
                  xs={12}
                  md={3}
                  key={col}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <ColumnHeader
                    style={{
                      background: columnColors[col]?.header,
                      borderBottom: `2px solid ${columnColors[col]?.header.split(' ')[2] || '#6fd943'}`
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        style={{ color: "white", fontWeight: 600 }}
                      >
                        {col}
                      </Typography>
                      <Box
                        sx={{
                          ml: 1,
                          backgroundColor: "rgba(255,255,255,0.1)",
                          color: "#ddd",
                          borderRadius: "12px",
                          px: 1.5,
                          py: 0.2,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {myTickets.filter((t) => t.status === col).length}
                      </Box>
                    </Box>
                  </ColumnHeader>
                  <Droppable droppableId={col}>
                    {(provided, snapshot) => (
                      <ColumnContainer
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          background: snapshot.isDraggingOver
                            ? "#232526"
                            : "#25282b"
                        }}
                      >
                        {myTickets
                          .filter((t) => t.status === col)
                          .map((ticket, index) => (
                            <Draggable
                              key={ticket.id}
                              draggableId={String(ticket.id)}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    marginBottom: 12,
                                    opacity: snapshot.isDragging ? 0.7 : 1,
                                  }}
                                >
                                  <AnimatedCard
                                    priority={ticket.priority}
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
                                      color: "white",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <CardContent sx={{ position: 'relative' }}>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 18,
                                          left: 90,
                                          background: '#232a34',
                                          border: '2px solid #6fd943',
                                          color: '#6fd943',
                                          borderRadius: '8px',
                                          px: 1.5,
                                          py: 0.2,
                                          fontWeight: 'bold',
                                          fontSize: 13,
                                          zIndex: 2,
                                          boxShadow: '0 2px 8px rgba(111,217,67,0.10)',
                                          fontFamily: 'Cairo, sans-serif',
                                        }}
                                      >
                                        #{ticket.id}
                                      </Box>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        color="#6fd943"
                                        mb={1}
                                      >
                                        <Typography
                                          variant="h6"
                                          fontWeight="bold"
                                          sx={{ fontSize: 18 }}
                                        >
                                          {ticket.title}
                                        </Typography>
                                        <Box
                                          sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 2,
                                            fontSize: 13,
                                            fontWeight: "bold",
                                            background:
                                              priorityColors[ticket.priority] ||
                                              "#1976d2",
                                            color: "#fff",
                                            minWidth: 60,
                                            textAlign: "center",
                                            boxShadow:
                                              "0 2px 4px rgba(0,0,0,0.2)",
                                          }}
                                        >
                                          {priorityLabels[ticket.priority] || ticket.priority}
                                        </Box>
                                      </Box>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: "#e0e0e0",
                                          fontWeight: 500,
                                          mb: 0.5,
                                        }}
                                      >
                                        الوصف: {ticket.description}
                                      </Typography>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: "#b0b0b0",
                                          fontWeight: 400,
                                          mb: 0.5,
                                        }}
                                      >
                                        الاسم: {ticket.username}
                                      </Typography>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: "#b0b0b0",
                                          fontWeight: 400,
                                          mb: 0.5,
                                        }}
                                      >
                                        الشركة: {ticket.company}
                                      </Typography>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: "#b0b0b0",
                                          fontWeight: 400,
                                          mb: 1,
                                        }}
                                      >
                                        القسم: {ticket.department}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="#a0a0a0"
                                        sx={{ display: "block", mt: 1 }}
                                      >
                                        {ticket.created_at}
                                      </Typography>
                                  
                                    </CardContent>
                                  </AnimatedCard>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </ColumnContainer>
                    )}
                  </Droppable>
                </Grid>
              ))}
            </Grid>
          </DragDropContext>
        )}
      
      </Paper>
    </Fade>
  );
};

export default TicketsForIT;
